import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useDesignStore, useProductSelectionStore, useUIStore } from '@/stores';
import type { CreatorEntryMode } from '@/stores/creatorSteps';
import { useBulkCreationStore } from '@/stores/bulkCreationStore';

import {
  BULK_ENTRY_PATH,
  CLASSIC_ENTRY_PATH,
  GUIDED_ENTRY_PATH,
} from '@/lib/creator-entry';

export { BULK_ENTRY_PATH, CLASSIC_ENTRY_PATH, GUIDED_ENTRY_PATH };

/**
 * Leaves no trace of a previously opened design before the Creator mounts:
 * the design store, the product selection and a single-product mode from
 * an earlier embedded start. The same sequence for both entries.
 *
 * The entry mode and the first step are set here as well, not only by
 * CreatorPage on mount: the creator routes share one CreatorPage instance,
 * so a switch from inside the Creator (the guided entry's advanced link, the
 * classic editor's way back) never mounts it again.
 */
function resetCreatorState(mode: CreatorEntryMode): void {
  useDesignStore.getState().clearDesign();
  useProductSelectionStore.getState().reset();
  const ui = useUIStore.getState();
  ui.setSingleProductMode(null);
  ui.setCreatorEntryMode(mode);
  ui.setCurrentStep('design');
}

/**
 * The ways to start new designs, with the reset each of them needs: the two
 * Creator entries, and Bulk Creation, which starts from an empty flow.
 */
export function useDesignEntry() {
  const navigate = useNavigate();

  return {
    startClassic: () => {
      resetCreatorState('classic');
      navigate(CLASSIC_ENTRY_PATH);
    },
    startGuided: () => {
      resetCreatorState('guided');
      navigate(GUIDED_ENTRY_PATH);
    },
    startBulk: () => {
      // The bulk store is not persisted, but it outlives a visit to the
      // overview; a new run never inherits the rows of an abandoned one.
      useBulkCreationStore.getState().reset();
      navigate(BULK_ENTRY_PATH);
    },
  };
}

/**
 * The design overview where the host owns the navigation (embed layout
 * `hostChrome`): the guided entry instead. Every way to `/designs` — after
 * a publish, leaving Bulk Creation — then starts a fresh guided design, and
 * the entry tiles never promote the classic editor or Bulk Creation there.
 */
export function HostHomeRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    resetCreatorState('guided');
    navigate(GUIDED_ENTRY_PATH, { replace: true });
  }, [navigate]);
  return null;
}

export interface DesignEntryChoiceProps {
  className?: string;
}

/**
 * Entry choice of the design overview (docs/design/seller-area/Main.dc.html):
 * a section card with three tiles. The classic entry is the primary one and
 * carries the 2 px primary ring; the guided entry and Bulk Creation sit
 * beside it on a hairline.
 */
export function DesignEntryChoice({ className }: DesignEntryChoiceProps) {
  const { t } = useTranslation();
  const { startClassic, startGuided, startBulk } = useDesignEntry();

  return (
    <section
      aria-label={t('seller.designs.entry.title')}
      className={cn(
        'grid gap-4 rounded-section bg-paper-2 p-5 ring-1 ring-line sm:grid-cols-2 lg:grid-cols-3',
        className
      )}
    >
      <EntryTile
        primary
        icon={<PlusIcon />}
        title={t('seller.designs.entry.classic')}
        hint={t('seller.designs.entry.classicHint')}
        onClick={startClassic}
      />
      <EntryTile
        icon={<WandIcon />}
        title={t('seller.designs.entry.guided')}
        hint={t('seller.designs.entry.guidedHint')}
        onClick={startGuided}
      />
      <EntryTile
        icon={<StackIcon />}
        title={t('seller.designs.entry.bulk')}
        hint={t('seller.designs.entry.bulkHint')}
        onClick={startBulk}
      />
    </section>
  );
}

interface EntryTileProps {
  icon: ReactNode;
  title: string;
  hint: string;
  onClick: () => void;
  /** Primary ring and filled icon box. */
  primary?: boolean;
}

function EntryTile({ icon, title, hint, onClick, primary }: EntryTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-4 rounded-card bg-paper px-5 py-[18px] text-left text-ink transition-colors duration-[120ms] hover:bg-paper-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-paper-2 active:translate-y-px',
        primary ? 'ring-2 ring-primary' : 'ring-1 ring-line'
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-control',
          primary
            ? 'bg-primary text-primary-fg'
            : 'bg-paper-2 text-ink ring-1 ring-line'
        )}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="dc-h2 text-xl text-ink">{title}</span>
        <span className="text-[13px] text-mute">{hint}</span>
      </span>
    </button>
  );
}

function PlusIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  );
}

function WandIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72" />
      <path d="m14 7 3 3" />
      <path d="M5 6v4" />
      <path d="M19 14v4" />
      <path d="M10 2v2" />
      <path d="M7 8H3" />
      <path d="M21 16h-4" />
      <path d="M11 3H9" />
    </svg>
  );
}

function StackIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m12 2 9 5-9 5-9-5 9-5Z" />
      <path d="m3 12 9 5 9-5" />
      <path d="m3 17 9 5 9-5" />
    </svg>
  );
}
